import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { SearchOutlined } from "@ant-design/icons";
import styled from "@emotion/styled";
import { Alert, Input, message, Select } from "antd5";
import { SortOrder } from "antd5/es/table/interface";
import { Flex, Text } from "styles/utility-components";

import UserInitials from "components/comments/UserInitials";
import { Checkbox, Select as FormSelect } from "components/form_components/Inputs";
import SendInviteModal from "components/modals/SendInviteModal";
import Button from "components/ui/button/Button";
import { Table } from "lib/core_components/Table";
import { ColumnType } from "lib/core_components/Table/ColumnTypes";
import { createUseDebounce } from "lib/debounce";
import { TEAM_USAGE_STATS, useVariableValue } from "lib/featureFlags";
import { useTeams } from "lib/hooks/api/teams/useTeams";
import { useCurrentUser } from "lib/hooks/api/users/useCurrentUser";
import { useUpdateUsers } from "lib/hooks/api/users/useUpdateUsers";
import {
  TeamManagementUser as User,
  useUserManagement,
} from "lib/hooks/api/users/useUserManagement";
import { usePreventNavigation } from "lib/hooks/usePreventNavigation";
import { useTablePageSize } from "lib/hooks/useTablePageSize";
import UIcon from "lib/icons/untitled_ui/UIcon";
import { useDialogManager } from "lib/providers/DialogManager";
import * as tracking from "lib/tracking";
import {
  TeamManagementPaymentType as PaymentType,
  TeamManagementUserSortBy as SortBy,
  TeamManagementUserSortOrder,
} from "lib/types/graphQLEnums";
import { EM_DASH, pluralise } from "lib/utils";
import { useSupportChat } from "lib/utils/useSupportChat";
import { useFormAutoSubmit } from "../../lib/hooks/useFormAutoSubmit";
import SkeletonText from "../ui/skeleton/SkeletonText";

import css from "./UserManagement.module.scss";

const PAYMENT_TYPE_LABELS = {
  [PaymentType.Freemium]: "Freemium",
  [PaymentType.Paid]: "Paid",
};

type UserFilters = {
  text: string;
  teamId: string;
  paymentType: PaymentType | typeof ALL_LICENSES;
};

const useDebounce = createUseDebounce(500);

const PAYMENT_TYPE_OPTIONS = {
  __ALL_LICENSES__: "All licenses",
  [PaymentType.Paid]: "Paid",
  [PaymentType.Freemium]: "Freemium",
} as const;

const ALL_TEAMS = "__ALL_TEAMS__";
const ALL_LICENSES = "__ALL_LICENSES__";

type FormValues = {
  users: Record<string, User>;
};

// Each sortable column's key is the field teamManagementUsers sorts by.
const SORTABLE_COLUMNS = [
  SortBy.Name,
  SortBy.Team,
  SortBy.CompanyAdmin,
  SortBy.PaymentType,
  SortBy.CreatedAt,
  SortBy.LastAccessedAt,
];
type SortState = { field: SortBy; order: TeamManagementUserSortOrder };

const DEFAULT_SORT: SortState = { field: SortBy.Name, order: TeamManagementUserSortOrder.Asc };

// Repeating the first direction makes a third click flip it back rather than clear the sort.
const ASCEND_FIRST: SortOrder[] = ["ascend", "descend", "ascend"];
const DESCEND_FIRST: SortOrder[] = ["descend", "ascend", "descend"];

function isSortField(key: unknown): key is SortBy {
  return SORTABLE_COLUMNS.some((field) => field === key);
}

function getSortOrder(sort: SortState, field: SortBy): SortOrder {
  if (sort.field !== field) {
    return null;
  }
  return sort.order === TeamManagementUserSortOrder.Asc ? "ascend" : "descend";
}

function UserManagement(): JSX.Element {
  const { openSupportChat } = useSupportChat();
  const dialogManager = useDialogManager();
  const [pageSize, setPageSize] = useTablePageSize("user-management-table");
  const [currentPage, setCurrentPage] = useState(1);
  const [filters, _setFilters] = useState<UserFilters>({
    text: "",
    teamId: ALL_TEAMS,
    paymentType: ALL_LICENSES,
  });
  const [textInputValue, setTextInputValue] = useState("");
  const [sort, setSort] = useState<SortState>(DEFAULT_SORT);
  const isTeamUsageStatsEnabled = useVariableValue(TEAM_USAGE_STATS, false);
  const originalUsersRef = useRef<Record<string, User>>({});
  const [editedUsers, setEditedUsers] = useState<Record<string, User>>({});

  const { control, watch, trigger, reset, getValues, handleSubmit } = useForm<FormValues>({
    defaultValues: { users: {} },
  });

  const handleFormValuesChange = useCallback((values: FormValues) => {
    setEditedUsers((prev) => {
      const next = { ...prev };

      for (const [guid, formUser] of Object.entries(values.users)) {
        const original = originalUsersRef.current[guid];
        if (!original) continue;

        const changed =
          original.team.id !== formUser.team.id || original.companyAdmin !== formUser.companyAdmin;

        if (changed) {
          next[guid] = formUser;
        } else {
          delete next[guid];
        }
      }

      return next;
    });
  }, []);

  useFormAutoSubmit({
    watch,
    getValues,
    trigger,
    onChange: handleFormValuesChange,
  });

  const { data: currentUser } = useCurrentUser();
  const isCompanyAdmin = currentUser?.isCompanyAdmin || currentUser?.isAdmin;

  // Hook to fetch the available teams within a company
  const {
    isLoading: loadingUsers,
    data: users,
    refetch,
    isError,
  } = useUserManagement(
    {
      text: filters.text ? filters.text.toLowerCase() : undefined,
      paymentType: filters.paymentType === ALL_LICENSES ? undefined : filters.paymentType,
      teamId: filters.teamId === ALL_TEAMS ? undefined : filters.teamId,
      sortBy: sort.field,
      sortOrder: sort.order,
      limit: pageSize,
      offset: (currentPage - 1) * pageSize,
    },
    {
      onError: async () => {
        await message.error("Unable to get users; please contact us if the problem persists");
      },
    },
  );

  const { mutate: updateUsers, isLoading: isUpdatingUsers } = useUpdateUsers({
    onSuccess: async () => {
      originalUsersRef.current = {};
      setEditedUsers({});
      await refetch();
      await message.success("Team updates successfully saved");
    },
    onError: async () => {
      await message.error("Failed to update teams; please contact us if the problem persists");
    },
  });

  const hasEdits = Object.keys(editedUsers).length > 0;

  usePreventNavigation(
    hasEdits,
    "Are you sure you want to leave the Team management page? Unsaved changes will be lost.",
  );

  const resetChanges = () => {
    reset();
    setEditedUsers({});
    originalUsersRef.current = {};
  };

  // Whenever users change (from API), hydrate the form values, but avoid overwriting
  // when changes aren't made. Also sets initial form values.
  useEffect(() => {
    if (!users?.results) return;

    const currentUsers = getValues("users") ?? {};

    const nextUsers = { ...currentUsers };

    for (const u of users.results) {
      // If we already have a value for this guid, keep it (may be edited). Set if
      // there is no team id (incomplete data)
      if (!nextUsers[u.guid] || !nextUsers[u.guid].team.id) {
        nextUsers[u.guid] = u;
      }

      if (!originalUsersRef.current[u.guid] || !originalUsersRef.current[u.guid].team.id) {
        originalUsersRef.current[u.guid] = u;
      }
    }

    // You *can* call reset – but you're not relying on isDirty anymore
    reset({ users: nextUsers });
  }, [users?.results, getValues, reset]);

  const { isLoading: loadingTeams, data: teams } = useTeams();

  const setFilters = useDebounce((f: UserFilters) => {
    _setFilters(f);
    setCurrentPage(1);
  });

  /**
   * Called on submit of the form
   * @param data
   * @returns
   */
  const onSubmit = async (data: FormValues) => {
    if (!users?.results) return;

    // Compare each user in the form with the original data
    const updatedUsers = Object.entries(data.users).flatMap(([guid, formUser]) => {
      const original = originalUsersRef.current[guid];
      if (!original) return [];

      const changed =
        original.team.id !== formUser.team.id || original.companyAdmin !== formUser.companyAdmin;

      if (!changed) return [];

      return {
        guid,
        firstName: formUser.firstName,
        lastName: formUser.lastName,
        teamId: formUser.team.id,
        isCompanyAdmin: formUser.companyAdmin,
      };
    });

    if (updatedUsers.length === 0) {
      await message.info("No changes detected.");
      return;
    }

    updateUsers({
      request: {
        updates: updatedUsers,
      },
    });
  };

  const COLUMNS = useMemo((): ColumnType<User>[] => {
    const sortable = (
      field: SortBy,
      sortDirections = ASCEND_FIRST,
    ): Pick<ColumnType<User>, "sorter" | "sortOrder" | "sortDirections"> => ({
      sorter: true,
      sortOrder: getSortOrder(sort, field),
      sortDirections,
    });

    let columns: ColumnType<User>[] = [
      {
        key: SortBy.Name,
        ...sortable(SortBy.Name),
        render: (_: unknown, user: User) => (
          <div className={css.nameCell}>
            <UserInitials
              firstName={user.firstName}
              lastName={user.lastName}
              className={css.userInitials}
            />
            <div className={css.userName}>
              {user.firstName} {user.lastName}
            </div>
            <div className={css.userEmail}>{user.email}</div>
          </div>
        ),
        title: "User",
      },
      {
        title: "Team",
        key: SortBy.Team,
        ...sortable(SortBy.Team),
        render: (_: unknown, user: User) =>
          isCompanyAdmin ? (
            <FormSelect
              control={control}
              label=""
              aria-label={`Team for ${user.firstName} ${user.lastName}`}
              name={`users.${user.guid}.team.id`}
              loading={loadingTeams || loadingUsers}
              options={
                teams?.map((t) => ({
                  key: t.id,
                  value: t.id,
                  label: t.name,
                })) || []
              }
              optionFilterProp="label"
            />
          ) : (
            <div>{user.team.name}</div>
          ),
      },
      {
        title: "Is user admin?",
        key: SortBy.CompanyAdmin,
        ...sortable(SortBy.CompanyAdmin),
        render: (_: unknown, user: User) => (
          <CheckboxContainer>
            <Checkbox
              name={`users.${user.guid}.companyAdmin`}
              control={control}
              label=""
              fieldLabel=""
              disabled={user.stotlesAdmin}
            />
          </CheckboxContainer>
        ),
      },
      {
        title: "License",
        key: SortBy.PaymentType,
        ...sortable(SortBy.PaymentType),
        render: (_: unknown, user: User) => {
          return <div>{PAYMENT_TYPE_LABELS[user.paymentType] || user.paymentType}</div>;
        },
      },

      {
        title: "Created",
        key: SortBy.CreatedAt,
        ...sortable(SortBy.CreatedAt, DESCEND_FIRST),
        render: (_: unknown, user: User) => <div>{user.createdAt.substring(0, 10)}</div>,
      },
      {
        title: "Last access",
        key: SortBy.LastAccessedAt,
        ...sortable(SortBy.LastAccessedAt, DESCEND_FIRST),
        // A dash rather than "Never": the user may have visited before auth-service recorded visits.
        render: (_: unknown, user: User) => (
          <div>{user.lastAccessedAt?.substring(0, 10) ?? EM_DASH}</div>
        ),
      },
    ];

    if (!isCompanyAdmin) {
      columns = columns.filter((column) => column.key !== SortBy.CompanyAdmin);
    }

    if (!isTeamUsageStatsEnabled) {
      columns = columns.filter((column) => column.key !== SortBy.LastAccessedAt);
    }

    return columns;
  }, [control, isCompanyAdmin, isTeamUsageStatsEnabled, loadingTeams, loadingUsers, sort, teams]);

  const filterElements = (
    <div className={css.filters}>
      <Input
        placeholder="Search users"
        prefix={<SearchOutlined />}
        value={textInputValue || undefined}
        onChange={(e) => {
          setTextInputValue(e.target.value);
          setFilters({ ...filters, text: e.target.value });
        }}
        allowClear
        className={css.input}
      />
      <Select
        value={filters.paymentType}
        onChange={(val) => {
          setFilters({ ...filters, paymentType: val });
        }}
        className={css.select}
      >
        {Object.entries(PAYMENT_TYPE_OPTIONS).map(([val, label]) => (
          <Select.Option key={val} value={val} label={label} title={label}>
            {label}
          </Select.Option>
        ))}
      </Select>
      <Select
        value={filters.teamId}
        loading={loadingTeams}
        onChange={(val) => {
          setFilters({ ...filters, teamId: val });
        }}
        className={css.select}
      >
        <Select.Option key="all" label="All teams" value="__ALL_TEAMS__">
          All teams
        </Select.Option>
        {teams?.map((t) => (
          <Select.Option key={t.id} label={t.name} value={t.id}>
            {t.name}
          </Select.Option>
        ))}
      </Select>
    </div>
  );

  return (
    <tracking.TrackingProvider
      data={{
        "Context source": "Account-Management - My Team",
      }}
    >
      <form className={css.teamsPageContainer} onSubmit={handleSubmit(onSubmit)}>
        <div className={css.teamMembersContainer}>
          <Flex column gap={24}>
            <div className={css.header}>
              <Text h1>Team management</Text>

              <span className={css.actions}>
                <Button
                  onClick={() => dialogManager.openDialog(SendInviteModal, {})}
                  icon="plus"
                  buttonType={isCompanyAdmin ? "secondary" : "primary"}
                >
                  Invite user
                </Button>
                {isCompanyAdmin && (
                  <>
                    <Button buttonType="secondary" onClick={resetChanges} disabled={!hasEdits}>
                      Discard Changes
                    </Button>
                    <Button type="submit" disabled={!hasEdits} loading={isUpdatingUsers}>
                      Save changes
                    </Button>
                  </>
                )}
                {/*
                  TODO: implement CSV download
                  <Button icon="download01">Download CSV</Button>
                  */}
              </span>
            </div>
            <Alert
              className={css.infoMessage}
              type="info"
              description={
                <Flex gap={8} alignItems="center">
                  <UIcon icon="infoCircle" color="sysPrimaryPressed" />
                  <Text color="sysPrimaryPressed">
                    Only user admins can move people between teams. To add/remove licenses or teams
                    please get in contact with Customer Support.
                  </Text>
                  <Button
                    buttonType="secondary"
                    onClick={() => openSupportChat("'Contact support' on user management page")}
                    type="button"
                  >
                    Contact support
                  </Button>
                </Flex>
              }
            />
            {filterElements}
            {loadingUsers ? (
              <SkeletonText width="80px" fontSize={16} />
            ) : (
              pluralise(users?.total || 0, "user")
            )}
            <Table
              ariaLabel="Team members table"
              columns={COLUMNS}
              dataSource={users?.results}
              loading={loadingUsers}
              pagination={{
                current: currentPage,
                pageSize,
                total: users?.total || 0,
                onChange: (page, pageSize) => {
                  setPageSize(pageSize);
                  setCurrentPage(page);
                },
                showSizeChanger: true,
              }}
              onChange={(_pagination, _filters, sorter, { action }) => {
                // Also fires on pagination, which the pagination onChange above handles.
                if (action !== "sort") return;

                const columnSorter = Array.isArray(sorter) ? sorter[0] : sorter;
                if (!isSortField(columnSorter?.columnKey) || !columnSorter.order) return;

                const { Asc, Desc } = TeamManagementUserSortOrder;
                const order = columnSorter.order === "ascend" ? Asc : Desc;
                setSort({ field: columnSorter.columnKey, order });
                setCurrentPage(1);
              }}
              rowKey="guid"
              isError={isError}
            />
          </Flex>
        </div>
      </form>
    </tracking.TrackingProvider>
  );
}

const CheckboxContainer = styled(Flex)({
  height: "100%",
  flexDirection: "column",
  alignItems: "center",
  marginBottom: 12,
});

export default UserManagement;
